import React from 'react'
import { To, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import filesStore from 'modules/documents/components/Documents/store'
import Folders from 'modules/documents/components/DocumentsAndFolders/FoldersList'
import DocumentsDataTable from 'modules/documents/components/DocumentsDatatable'
import DocumentsResourceHeader from 'modules/documents/components/DocumentsResourceHeader'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import type { CommonRouteContext } from 'modules/employees/lib/types'

interface Props {
  parentRoute: To
  canUploadDocument: boolean
}

const DocumentsAndFolders = ({ parentRoute, canUploadDocument }: Props) => {
  const { employee } = useOutletContext<CommonRouteContext>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedFolder, setSelectedFolder] = React.useState<Folder | null>(null)
  const employeeId = employee.id
  const isCurrentEmployee = employeeUser(employee).isCurrent()

  const newDocumentUrl = isCurrentEmployee
    ? navigation.documents.list.new.fullPath()
    : navigation.employees.id.documents.list.new.fullPath({ id: employeeId })

  const onDrop = (files: File[]) => {
    if (!canUploadDocument) return null

    setSelectedFolder(null)
    filesStore.setFiles(files)

    navigate(newDocumentUrl)
  }

  const onUpload = () => {
    setSelectedFolder(selectedFolder)
    navigate(newDocumentUrl)
  }

  const onNavigationClick = (documentId: number) => {
    const documentUrl = isCurrentEmployee
      ? navigation.documents.list.documentId.fullPath({ document_id: documentId })
      : navigation.employees.id.documents.list.documentId.fullPath({
          id: employeeId,
          document_id: documentId,
        })

    navigate(documentUrl)
  }

  return (
    <>
      <DocumentsResourceHeader
        title={i18n.t('documents.smart.title_v2')}
        canUploadDocument={canUploadDocument}
        url={newDocumentUrl}
        removePadding
      />
      <Box paddingTop="s8">
        <Folders
          onFolderSelected={setSelectedFolder}
          space={SPACES.EMPLOYEE_MY_DOCUMENTS}
          employee={employee}
          readonly={!canUploadDocument}
        />
      </Box>
      <Box gap="s8" paddingTop="s8">
        <DocumentsDataTable
          employee={employee}
          folder={null}
          initialFolder={selectedFolder}
          parentRoute={parentRoute}
          readonly={!canUploadDocument}
          onDrop={onDrop}
          onNavigationClick={onNavigationClick}
          onUpload={onUpload}
        />
      </Box>
    </>
  )
}

export default DocumentsAndFolders
